import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import AnimatedSection from '../../components/base/AnimatedSection';
import SEOHead from '../../components/base/SEOHead';
import GatewayOverviewContent from './components/GatewayOverviewContent';
import ConfigContent from './components/ConfigContent';
import AuthContent from './components/AuthContent';
import SecurityContent from './components/SecurityContent';
import RemoteAccessContent from './components/RemoteAccessContent';
import LogsContent from './components/LogsContent';
import SandboxContent from './components/SandboxContent';
import TroubleshootingContent from './components/TroubleshootingContent';

interface MenuItem {
  id: string;
  label: string;
  component: React.ComponentType;
}

interface MenuGroup {
  title: string;
  items: MenuItem[];
}

const menuGroups: MenuGroup[] = [
  {
    title: '概览',
    items: [
      { id: 'overview', label: '网关概览', component: GatewayOverviewContent }
    ]
  },
  {
    title: '配置与认证',
    items: [
      { id: 'config', label: '配置', component: ConfigContent },
      { id: 'auth', label: '认证', component: AuthContent },
      { id: 'security', label: '安全', component: SecurityContent }
    ]
  },
  {
    title: '运维管理',
    items: [
      { id: 'remote-access', label: '远程访问', component: RemoteAccessContent },
      { id: 'logs', label: '日志', component: LogsContent },
      { id: 'sandbox', label: '沙箱', component: SandboxContent },
      { id: 'troubleshooting', label: '故障排除', component: TroubleshootingContent }
    ]
  }
];

export default function GatewayPage() {
  const [activeItem, setActiveItem] = useState(() => {
    return localStorage.getItem('gateway-active-item') || 'overview';
  });
  
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://openclaw.cc';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: '网关与运维',
    description: 'OpenClaw Gateway 网关配置、认证、安全、日志、故障排除完整指南',
    url: `${siteUrl}/gateway`,
    datePublished: '2024-01-01',
    dateModified: new Date().toISOString().split('T')[0],
    author: {
      '@type': 'Organization',
      name: 'OpenClaw',
    },
    publisher: {
      '@type': 'Organization',
      name: 'OpenClaw',
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/lobster.svg`,
      },
    },
    inLanguage: 'zh-CN',
  };

  // 监听 localStorage 变化，响应导航栏的子菜单跳转
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'gateway_active_section' && e.newValue) {
        setActiveItem(e.newValue);
      }
    };

    const handleNavChange = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.storageKey === 'gateway_active_section') {
        setActiveItem(detail.key);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('nav-section-change', handleNavChange);

    // 页面加载时检查
    const saved = localStorage.getItem('gateway_active_section');
    if (saved) setActiveItem(saved);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('nav-section-change', handleNavChange);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('gateway-active-item', activeItem);
  }, [activeItem]);

  const handleItemClick = (itemId: string) => {
    if (itemId === activeItem) return;
    setActiveItem(itemId);
    setMobileMenuOpen(false);
  };

  const ActiveComponent = menuGroups
    .flatMap((group) => group.items)
    .find((item) => item.id === activeItem)?.component || GatewayOverviewContent;

  // 获取所有菜单项（扁平化）
  const allMenuItems = menuGroups.flatMap((group) => group.items);
  const currentNavItem = allMenuItems.find(item => item.id === activeItem);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <SEOHead
        title="网关与运维 | OpenClaw Gateway 配置、安全、监控"
        description="OpenClaw Gateway 网关配置、认证、安全、日志、故障排除完整指南 - 保障智能体稳定运行"
        keywords="OpenClaw网关, Gateway配置, API认证, 安全配置, 日志监控, 故障排除"
        canonical={`${siteUrl}/gateway`}
        jsonLd={jsonLd}
      />
      <Navbar />
      
      <main className="pt-24 pb-20">
        <div className="max-w-[1600px] mx-auto px-10 md:px-20">
          {/* Breadcrumb */}
          <AnimatedSection className="mb-8">
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <a href="/" className="hover:text-green-400 transition-colors">首页</a>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-white">网关与运维</span>
            </div>
          </AnimatedSection>

          {/* Page Header */}
          <AnimatedSection className="mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/20 rounded-full mb-6">
              <i className="ri-shield-check-line text-green-400"></i>
              <span className="text-sm text-green-400 font-medium">网关与运维</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-green-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent">
              OpenClaw 网关
            </h1>
            <p className="text-xl text-slate-400 max-w-3xl leading-relaxed">
              统一的 API 网关，提供认证、路由、限流和监控功能，让你的 AI 智能体安全可靠地对外服务
            </p>
          </AnimatedSection>

          {/* 手机端顶部 Tab 选择器 */}
          <div className="md:hidden mb-6 overflow-x-auto scrollbar-hide">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="w-full flex items-center justify-between px-4 py-3 bg-slate-900/50 backdrop-blur-sm border border-slate-800/50 rounded-2xl text-left cursor-pointer mb-3"
            >
              <div className="flex items-center gap-2.5">
                <span className="text-white font-medium text-sm">{currentNavItem?.label}</span>
              </div>
              <i className={`ri-arrow-down-s-line text-slate-400 transition-transform duration-200 ${mobileMenuOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {mobileMenuOpen && (
              <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800/50 rounded-2xl p-3 mb-3">
                {allMenuItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleItemClick(item.id)}
                    className={`w-full text-left px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap mb-1 ${
                      activeItem === item.id
                        ? 'bg-gradient-to-r from-green-500/20 to-cyan-500/20 text-green-400 border border-green-500/30'
                        : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800/50 rounded-2xl p-6 md:p-8">
              <ActiveComponent />
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}